import { zodResolver } from "@hookform/resolvers/zod";
import { Action } from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Ban, Loader2, Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	getReceivableQueryOptions,
	useAddReceipt,
	useDeleteReceipt,
	useDeleteReceivable,
} from "@/api/financial";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { toCents } from "@/utils/decimal";
import { formatMoney } from "@/utils/financial";
import { dateInput, money } from "@/utils/formSchemas";
import { ReceivableStatusBadge } from "./-components/ReceivableBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/receivables/$receivableId",
)({
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getReceivableQueryOptions(params.orgId, params.receivableId),
		);
	},
	component: ReceivableDetailPage,
});

function ReceivableDetailPage() {
	const { orgId, receivableId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [isAddOpen, setIsAddOpen] = useState(false);
	const [deletingReceiptId, setDeletingReceiptId] = useState<string | null>(
		null,
	);
	const [isRemoveFlagOpen, setIsRemoveFlagOpen] = useState(false);

	const { data: receivable } = useSuspenseQuery(
		getReceivableQueryOptions(orgId, receivableId),
	);

	const addReceiptMutation = useAddReceipt();
	const deleteReceiptMutation = useDeleteReceipt();
	const deleteReceivableMutation = useDeleteReceivable();

	const currency = receivable.currency;
	const isVoid = receivable.isVoid;
	const canAddReceipt = canManage && !isVoid && receivable.status !== "SETTLED";

	return (
		<div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
			<PageHeader title={receivable.buyerName} showBackButton />

			{isVoid && (
				<Card className="border-error/40 bg-error/5">
					<CardContent className="flex items-center gap-3 p-4">
						<Ban className="h-5 w-5 text-error" />
						<div>
							<p className="font-medium text-error">
								{t("financial.receivables.void.title")}
							</p>
							<p className="text-sm text-base-content/70">
								{t("financial.receivables.void.description")}
							</p>
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardContent className="flex flex-col gap-4 p-5">
					<div className="flex items-start justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<ReceivableStatusBadge status={receivable.displayStatus} />
							<Link
								to="/app/org/$orgId/livestock/$establishmentId"
								params={{
									orgId,
									establishmentId: receivable.establishmentId,
								}}
								className="text-sm font-medium text-primary hover:underline"
							>
								{t("financial.receivables.viewSale")}
							</Link>
						</div>
						<span className="text-xl font-semibold">
							{formatMoney(receivable.outstandingAmount, currency)}
						</span>
					</div>

					{receivable.buyerDocument && (
						<Row
							label={t("financial.receivables.buyerDocument")}
							value={receivable.buyerDocument}
						/>
					)}
					<Row
						label={t("financial.receivables.saleDate")}
						value={formatDateOnly(new Date(receivable.saleDate).getTime())}
					/>
					<Row
						label={t("financial.receivables.dueDate")}
						value={
							receivable.dueDate
								? formatDateOnly(new Date(receivable.dueDate).getTime())
								: "—"
						}
					/>
					<Row
						label={t("financial.receivables.expected")}
						value={formatMoney(receivable.expectedAmount, currency)}
					/>
					<Row
						label={t("financial.receivables.received")}
						value={formatMoney(receivable.receivedAmount, currency)}
					/>
					<Row
						label={t("financial.receivables.outstanding")}
						value={formatMoney(receivable.outstandingAmount, currency)}
					/>
					{receivable.notes && (
						<Row
							label={t("financial.receivables.notes")}
							value={receivable.notes}
						/>
					)}
				</CardContent>
			</Card>

			{canManage && (
				<div className="flex flex-wrap gap-2">
					{canAddReceipt && (
						<Button onClick={() => setIsAddOpen(true)}>
							<Plus className="mr-2 h-4 w-4" />
							{t("financial.receivables.receipts.add.action")}
						</Button>
					)}
					<Button
						variant="outline"
						className="text-error"
						onClick={() => setIsRemoveFlagOpen(true)}
						disabled={deleteReceivableMutation.isPending}
					>
						<Ban className="mr-2 h-4 w-4" />
						{t("financial.receivables.delete.action")}
					</Button>
				</div>
			)}

			<Card>
				<CardContent className="p-0">
					{receivable.receipts.length === 0 ? (
						<p className="py-8 text-center text-sm text-base-content/60">
							{t("financial.receivables.receipts.empty")}
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.receivables.receipts.columns.date")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.receivables.receipts.columns.amount")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.receivables.receipts.columns.notes")}
									</TableHead>
									{canManage && <TableHead className="text-right" />}
								</TableRow>
							</TableHeader>
							<TableBody>
								{receivable.receipts.map((receipt) => (
									<TableRow key={receipt.id}>
										<TableCell>
											{formatDateOnly(new Date(receipt.receivedDate).getTime())}
										</TableCell>
										<TableCell className="text-right font-medium text-success">
											{formatMoney(receipt.amount, currency)}
										</TableCell>
										<TableCell className="hidden sm:table-cell text-base-content/70">
											{receipt.notes ?? "—"}
										</TableCell>
										{canManage && (
											<TableCell className="text-right">
												<Button
													size="sm"
													variant="ghost"
													className="text-error"
													onClick={() => setDeletingReceiptId(receipt.id)}
												>
													<Trash2 className="h-3 w-3" />
												</Button>
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{isAddOpen && (
				<AddReceiptDialog
					defaultAmount={receivable.outstandingAmount}
					isSubmitting={addReceiptMutation.isPending}
					onClose={() => setIsAddOpen(false)}
					onConfirm={(data) =>
						addReceiptMutation
							.mutateAsync({ organizationId: orgId, receivableId, data })
							.then(() => setIsAddOpen(false))
					}
				/>
			)}

			<AlertDialog
				open={deletingReceiptId !== null}
				onOpenChange={(open) => !open && setDeletingReceiptId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.receivables.receipts.delete.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.receivables.receipts.delete.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteReceiptMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (!deletingReceiptId) return;
								return deleteReceiptMutation
									.mutateAsync({
										organizationId: orgId,
										receivableId,
										receiptId: deletingReceiptId,
									})
									.then(() => setDeletingReceiptId(null));
							}}
							disabled={deleteReceiptMutation.isPending}
						>
							{deleteReceiptMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.receivables.receipts.delete.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={isRemoveFlagOpen} onOpenChange={setIsRemoveFlagOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.receivables.delete.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.receivables.delete.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteReceivableMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								deleteReceivableMutation
									.mutateAsync({ organizationId: orgId, receivableId })
									.then(() => {
										setIsRemoveFlagOpen(false);
										navigate({
											to: "/app/org/$orgId/financial/receivables",
											params: { orgId },
											replace: true,
										});
									})
							}
							disabled={deleteReceivableMutation.isPending}
						>
							{deleteReceivableMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.receivables.delete.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-start justify-between gap-3 border-b border-base-200 pb-2 text-sm last:border-0">
			<span className="text-base-content/60">{label}</span>
			<span className="text-right font-medium">{value}</span>
		</div>
	);
}

const addReceiptSchema = z.object({
	amount: money(),
	receivedDate: dateInput(),
	notes: z.string(),
});
type AddReceiptInput = z.input<typeof addReceiptSchema>;
type AddReceiptOutput = z.output<typeof addReceiptSchema>;

function AddReceiptDialog({
	defaultAmount,
	isSubmitting,
	onConfirm,
	onClose,
}: {
	defaultAmount: string;
	isSubmitting: boolean;
	onConfirm: (data: {
		amount: string;
		receivedDate: string;
		notes?: string;
	}) => Promise<unknown> | undefined;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { todayForDateInput } = useDateFormatter();
	const formId = useId();
	const schema = addReceiptSchema.extend({
		amount: money().refine(
			(value) => (toCents(value) ?? 0) <= (toCents(defaultAmount) ?? 0),
			"forms.validation.exceedsOutstanding",
		),
	});

	const form = useForm<AddReceiptInput, unknown, AddReceiptOutput>({
		resolver: zodResolver(schema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			amount: defaultAmount,
			receivedDate: todayForDateInput(),
			notes: "",
		},
	});

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		onConfirm({
			amount: values.amount,
			receivedDate: new Date(values.receivedDate).toISOString(),
			...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
		}),
	);

	return (
		<ResponsiveFormDialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("financial.receivables.receipts.add.title")}
			description={t("financial.receivables.receipts.add.description")}
			pending={isSubmitting}
			footer={
				<>
					<Button
						type="button"
						variant="outline"
						disabled={isSubmitting}
						onClick={onClose}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton form={formId} pending={isSubmitting}>
						{t("financial.receivables.receipts.add.confirm")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={formId} className="space-y-4 py-2" onSubmit={guardedSubmit}>
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.receivables.receipts.add.amount")}
								</FormLabel>
								<FormControl>
									<NumberField
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										ref={field.ref}
										suffix="R$"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="receivedDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.receivables.receipts.add.receivedDate")}
								</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="notes"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("financial.receivables.receipts.add.notes")}
								</FormLabel>
								<FormControl>
									<Textarea {...field} rows={2} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
